import {
    AccountBalanceWallet as CashDrawerIcon,
    ExpandMore as ExpandMoreIcon,
    Print as PrintIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useModules } from '../contexts/ModulesContext';
import {
    apiErrorKey,
    asksAtHandover,
    E_INVOICE_RUN_PATH,
    type EInvoiceSummary,
} from '../domain/eInvoice';
import { moduleUnavailableReason } from '../domain/modules';
import useRequest from '../hooks/useRequest';
import CassaDiffRegistry from '../components/CassaDiffRegistry';

interface Denominations {
    ron500: number;
    ron200: number;
    ron100: number;
    ron50: number;
    ron20: number;
    ron10: number;
    ron5: number;
    ron1: number;
    bani50: number;
    bani10: number;
    bani5: number;
    bani1: number;
}

interface BalanceInfo {
    previousBalance: number;
    intrari: number;
    iesiri: number;
    dispozitiiIntrari: number;
    dispozitiiIesiri: number;
    transfersIn: number;
    transfersOut: number;
    expectedBalance: number;
}

interface User {
    id: number;
    username: string;
}

interface CashPoint {
    id: number;
    name: string;
}

interface SessionInfo {
    sessionId: number;
    sessionNumber: number;
    cashPointId: number;
    userId: number;
    startTime: Date;
}

interface MovementSummary {
    tip?: string;
    count: number;
    total: number;
}

interface InvoiceDetail {
    tip: string;
    NrContract: string;
    NrFactura: string;
    SumainLei: number;
    Ora: string;
    UserId: number;
    Cassa_Session_Id: number;
}

interface DispozitiDetail {
    tip: string;
    Reprezentand: string;
    Suma: number;
    Ora: string;
    User: string;
    Cassa_Session_Id: number;
}

interface ContractDetail {
    NrContract: string;
    SumainLei: number;
    Ora: string;
    ReportDate?: string;
    UserId: number;
    Cassa_Session_Id: number;
}

type MovementDetail = InvoiceDetail | DispozitiDetail | ContractDetail;

interface MovementGroup {
    type: string;
    summary: MovementSummary[];
    details: MovementDetail[];
}

interface SessionMovements {
    intrari: MovementGroup[];
    iesiri: MovementGroup[];
}

/**
 * Format a time string (HH:mm:ss) for display.
 * Falls back to displaying the time directly if it's a simple time string.
 */
const formatMovementTime = (ora: string | undefined, reportDate?: string): string => {
    if (!ora) return '-';

    // If we have a reportDate, combine with time for full datetime
    if (reportDate) {
        const fullDate = new Date(`${reportDate}T${ora}`);
        if (!isNaN(fullDate.getTime())) {
            return fullDate.toLocaleTimeString();
        }
    }

    // If ora is just a time string like "14:30:00", display it directly
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(ora)) {
        return ora.substring(0, 5); // Return HH:mm format
    }

    // Try to parse as a full date (for invoice times that might include date)
    const date = new Date(ora);
    if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString();
    }

    return ora; // Return as-is if nothing works
};

const CashDrawer = () => {
    const { t } = useTranslation();
    const { user: authUser } = useAuth();
    const { refreshBalance, cashPointId: stationCashPointId } = useCashDrawer();
    const { modules } = useModules();
    const request = useRequest();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [eInvoicePromptOpen, setEInvoicePromptOpen] = useState(false);
    const [eInvoiceStarting, setEInvoiceStarting] = useState(false);

    // Session and user info
    const [sessionId, setSessionId] = useState<number>(0);
    const [sessionNumber, setSessionNumber] = useState<number>(0);
    const [cashPointId, setCashPointId] = useState<number>(0);
    const [userId, setUserId] = useState<number>(0);
    const [users, setUsers] = useState<User[]>([]);
    const [cashPoints, setCashPoints] = useState<CashPoint[]>([]);

    // Handover data
    const [fromUser, setFromUser] = useState<string>('');
    const [toUser, setToUser] = useState<string>('');
    const [denominations, setDenominations] = useState<Denominations>({
        ron500: 0,
        ron200: 0,
        ron100: 0,
        ron50: 0,
        ron20: 0,
        ron10: 0,
        ron5: 0,
        ron1: 0,
        bani50: 0,
        bani10: 0,
        bani5: 0,
        bani1: 0,
    });

    const [euroAmount, setEuroAmount] = useState<number>(0);
    const [usdAmount, setUsdAmount] = useState<number>(0);
    const [notesRon, setNotesRon] = useState<string>('');
    const [notesForeignCurrency, setNotesForeignCurrency] = useState<string>('');

    // Transfer options
    const [withTransfer, setWithTransfer] = useState(false);
    const [transferToCashPointId, setTransferToCashPointId] = useState<number | undefined>();
    const [transferToUserId, setTransferToUserId] = useState<number | undefined>();

    // Balance info
    const [balanceInfo, setBalanceInfo] = useState<BalanceInfo | null>(null);
    const [actualAmount, setActualAmount] = useState<number>(0);
    const [difference, setDifference] = useState<number>(0);

    // Movements dialog
    const [showMovements, setShowMovements] = useState(false);
    const [movements, setMovements] = useState<SessionMovements | null>(null);

    // Report number
    const [reportNumber, setReportNumber] = useState<number>(1);

    /** Extract the monetary amount from a movement detail (avoids nested ternaries). */
    const getDetailAmount = (detail: MovementDetail): number => {
        if ('SumainLei' in detail) return detail.SumainLei;
        if ('Suma' in detail) return detail.Suma;
        return 0;
    };

    /** Extract ReportDate when present on a detail (avoids unnecessary type assertion). */
    const getReportDate = (detail: MovementDetail): string | undefined =>
        'ReportDate' in detail ? detail.ReportDate : undefined;

    /** Build a stable key for a movement detail row. */
    const detailKey = (detail: MovementDetail, idx: number) => {
        if ('NrContract' in detail && 'NrFactura' in detail)
            return `${detail.NrContract}-${detail.NrFactura}-${idx}`;
        if ('NrContract' in detail) return `${detail.NrContract}-${idx}`;
        if ('Reprezentand' in detail) return `${detail.Reprezentand}-${idx}`;
        return `detail-${idx}`;
    };

    /** Build a stable key for a summary chip. */
    const summaryKey = (item: MovementSummary, idx: number) =>
        item.tip ? `${item.tip}-${idx}` : `summary-${idx}`;

    /** Render a single movement group accordion (shared between income & expenses). */
    const renderMovementGroup = (group: MovementGroup, variant: 'income' | 'expense') => {
        const chipColor = variant === 'income' ? 'success' : 'error';
        const amountPrefix = variant === 'income' ? '+' : '-';
        const amountSx =
            variant === 'income'
                ? { fontWeight: 'bold', color: 'success.main' }
                : { fontWeight: 'bold', color: 'error.main' };

        return (
            <Accordion key={group.type} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <Typography fontWeight="bold">
                            {t(`cashDrawer.movements.${group.type}`)}
                        </Typography>
                        <Chip
                            label={`${group.summary.reduce((acc, item) => acc + Number(item.count ?? 0), 0)} ${t('cashDrawer.movements.items')}`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip
                            label={`${group.summary.reduce((acc, item) => acc + Number(item.total ?? 0), 0).toFixed(0)} RON`}
                            color={chipColor}
                            size="small"
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {/* Summary by type */}
                    {group.summary.length > 0 && group.summary[0].tip && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('cashDrawer.movements.summary')}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {group.summary.map((item, idx) => (
                                    <Chip
                                        key={summaryKey(item, idx)}
                                        label={`${item.tip}: ${Number(item.count)} × ${Number(item.total).toFixed(0)} RON`}
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {/* Details table */}
                    {group.details.length > 0 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('cashDrawer.movements.time')}</TableCell>
                                        {group.type === 'facturi' && (
                                            <>
                                                <TableCell>
                                                    {t('cashDrawer.movements.type')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('cashDrawer.movements.contractNumber')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('cashDrawer.movements.invoiceNumber')}
                                                </TableCell>
                                            </>
                                        )}
                                        {group.type === 'contracte' && (
                                            <TableCell>
                                                {t('cashDrawer.movements.contractNumber')}
                                            </TableCell>
                                        )}
                                        {group.type === 'dispozitii' && (
                                            <>
                                                <TableCell>
                                                    {t('cashDrawer.movements.type')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('cashDrawer.movements.description')}
                                                </TableCell>
                                                <TableCell>
                                                    {t('cashDrawer.movements.user')}
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell align="right">
                                            {t('cashDrawer.movements.amount')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {group.details.map((detail, idx) => (
                                        <TableRow key={detailKey(detail, idx)}>
                                            <TableCell>
                                                {formatMovementTime(
                                                    'Ora' in detail ? detail.Ora : undefined,
                                                    getReportDate(detail),
                                                )}
                                            </TableCell>
                                            {group.type === 'facturi' &&
                                                'NrContract' in detail &&
                                                'tip' in detail && (
                                                    <>
                                                        <TableCell>{detail.tip}</TableCell>
                                                        <TableCell>{detail.NrContract}</TableCell>
                                                        <TableCell>
                                                            {'NrFactura' in detail
                                                                ? detail.NrFactura
                                                                : '-'}
                                                        </TableCell>
                                                    </>
                                                )}
                                            {group.type === 'contracte' &&
                                                'NrContract' in detail && (
                                                    <TableCell>{detail.NrContract}</TableCell>
                                                )}
                                            {group.type === 'dispozitii' &&
                                                'Reprezentand' in detail && (
                                                    <>
                                                        <TableCell>{detail.tip}</TableCell>
                                                        <TableCell>{detail.Reprezentand}</TableCell>
                                                        <TableCell>{detail.User}</TableCell>
                                                    </>
                                                )}
                                            <TableCell align="right" sx={amountSx}>
                                                {amountPrefix}
                                                {Number(getDetailAmount(detail)).toFixed(0)} RON
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {group.details.length === 0 && (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('cashDrawer.movements.noMovements')}
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    // Calculate actual amount from denominations
    const calculateActualAmount = useCallback(() => {
        const amount =
            denominations.ron500 * 500 +
            denominations.ron200 * 200 +
            denominations.ron100 * 100 +
            denominations.ron50 * 50 +
            denominations.ron20 * 20 +
            denominations.ron10 * 10 +
            denominations.ron5 * 5 +
            denominations.ron1 * 1 +
            denominations.bani50 * 0.5 +
            denominations.bani10 * 0.1 +
            denominations.bani5 * 0.05 +
            denominations.bani1 * 0.01;
        setActualAmount(Number(amount.toFixed(2)));
    }, [denominations]);

    // Update actual amount when denominations change
    useEffect(() => {
        calculateActualAmount();
    }, [calculateActualAmount]);

    // Calculate difference
    useEffect(() => {
        if (balanceInfo) {
            const diff = actualAmount - Number(balanceInfo.expectedBalance);
            setDifference(Number(diff.toFixed(2)));
        }
    }, [actualAmount, balanceInfo]);

    // Load initial data
    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current user from auth context
            const currentUserId = authUser?.id ? parseInt(authUser.id) : 0;
            const currentUsername = authUser?.username ?? '';

            setUserId(currentUserId);
            setFromUser(currentUsername);

            // Load users from API
            const usersData = await request<User[]>({
                path: '/cash-drawer/users',
            });
            setUsers(usersData);

            // Load cashpoints from API
            const cashPointsData = await request<CashPoint[]>({
                path: '/cash-drawer/cashpoints',
            });
            setCashPoints(cashPointsData);

            // The drawer handed over is this workstation's register, as App registered it at
            // startup. Taking the first register of the name-ordered list instead handed over
            // a register named "1" - no handovers, no movements, so a previous balance of 0 -
            // and opened a stray session on it.
            const stationRegisterId = stationCashPointId ?? 0;
            setCashPointId(stationRegisterId);

            // Get or create current session
            if (currentUserId && stationRegisterId) {
                try {
                    // Use POST endpoint that will create session if it doesn't exist
                    const sessionData = await request<SessionInfo & { isNewSession?: boolean }>({
                        path: '/cash-drawer/session',
                        method: 'POST',
                        params: { cashPointId: stationRegisterId },
                    });
                    if (sessionData?.sessionId) {
                        setSessionId(sessionData.sessionId);
                        setSessionNumber(sessionData.sessionNumber);
                        setCashPointId(sessionData.cashPointId);

                        if (sessionData.isNewSession) {
                            console.info('New session created:', sessionData.sessionId);
                        }

                        // Load expected balance for this session
                        const balanceData = await request<BalanceInfo>({
                            path: `/cash-drawer/expected-balance/${sessionData.sessionNumber}/${sessionData.cashPointId}`,
                        });
                        setBalanceInfo(balanceData);
                    }
                } catch (sessionError) {
                    // Log session error but continue - handover still requires session
                    console.error('Failed to get or create session:', sessionError);
                }
            }

            // Load daily report number
            const countData = await request<{ count: number }>({
                path: '/cash-drawer/daily-count',
                params: { date: new Date().toISOString().split('T')[0] },
            });
            setReportNumber(countData.count);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
            console.error('Load error:', err);
        } finally {
            setLoading(false);
        }
    }, [request, authUser, stationCashPointId]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const handleDenominationChange = (key: keyof Denominations, value: string) => {
        const numValue = parseInt(value) || 0;
        setDenominations(prev => ({
            ...prev,
            [key]: numValue >= 0 ? numValue : 0,
        }));
    };

    const handleViewMovements = async () => {
        try {
            const data = await request<SessionMovements>({
                path: `/cash-drawer/session-movements/${sessionNumber}`,
            });
            setMovements(data);
            setShowMovements(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load movements');
        }
    };

    /** As Delphi asked "Doriti exportarea eFacturilor?" when the day was closed. */
    const askToSendEInvoices = async () => {
        const available = moduleUnavailableReason(modules, 'e-invoice') === null;

        try {
            const summary = available
                ? (await request<{ data: EInvoiceSummary }>({ path: '/e-invoice/summary' })).data
                : undefined;

            setEInvoicePromptOpen(asksAtHandover(available, summary));
        } catch (err) {
            // The handover is saved; a question that cannot be asked must not look like a failure.
            console.error('Reading the e-invoice summary failed:', err);
        }
    };

    const sendEInvoices = async () => {
        setEInvoiceStarting(true);
        try {
            await request({
                path: E_INVOICE_RUN_PATH,
                method: 'POST',
                params: { source: 'HANDOVER' },
            });
            setSuccess(t('eInvoice.run.started'));
            setSnackbarOpen(true);
        } catch (err) {
            setError(t(apiErrorKey(err, 'eInvoice.run.failed')));
        } finally {
            setEInvoiceStarting(false);
            setEInvoicePromptOpen(false);
        }
    };

    const handleSaveHandover = async () => {
        if (!toUser || toUser === fromUser) {
            setError(t('cashDrawer.selectDifferentUser'));
            return;
        }

        // Validate required session data
        if (sessionId === 0 || sessionNumber === 0) {
            setError(t('cashDrawer.noActiveSession'));
            return;
        }

        if (cashPointId === 0) {
            setError(t('cashDrawer.noCashPointSelected'));
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const requestParams = {
                sessionId,
                sessionNumber,
                cashPointId,
                userId,
                fromUser,
                toUser,
                denominations,
                actualAmount,
                euroAmount,
                usdAmount,
                notesRon,
                notesForeignCurrency,
                withTransfer,
                transferToCashPointId,
                transferToUserId,
            };
            console.log('Handover request params:', requestParams);

            await request<{ success: boolean; handover: unknown; message: string }>({
                path: '/cash-drawer/handover',
                method: 'POST',
                params: requestParams,
            });

            // Refresh cash drawer balance immediately after successful handover
            refreshBalance();

            setSuccess(t('cashDrawer.handoverComplete'));
            setSnackbarOpen(true);
            void askToSendEInvoices();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save handover');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <PageHeaderBanner
                icon={<CashDrawerIcon />}
                title={`${t('cashDrawer.title')} - ${t('cashDrawer.reportNumber')}: ${reportNumber}`}
                subtitle={t('cashDrawer.subtitle')}
                accentColor="#15803d"
            />

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left Column - Denominations */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('cashDrawer.denominations')}
                            </Typography>

                            <Grid container spacing={2}>
                                {/* Bills */}
                                <Grid size={12}>
                                    <Typography variant="subtitle2" color="primary">
                                        {t('cashDrawer.bills')}
                                    </Typography>
                                </Grid>
                                {[
                                    { key: 'ron500', value: 500 },
                                    { key: 'ron200', value: 200 },
                                    { key: 'ron100', value: 100 },
                                    { key: 'ron50', value: 50 },
                                    { key: 'ron20', value: 20 },
                                    { key: 'ron10', value: 10 },
                                    { key: 'ron5', value: 5 },
                                    { key: 'ron1', value: 1 },
                                ].map(item => (
                                    <Grid size={6} key={item.key}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={`${item.value} RON`}
                                            value={denominations[item.key as keyof Denominations]}
                                            onChange={e =>
                                                handleDenominationChange(
                                                    item.key as keyof Denominations,
                                                    e.target.value,
                                                )
                                            }
                                            slotProps={{ htmlInput: { min: 0 } }}
                                            size="small"
                                        />
                                    </Grid>
                                ))}

                                {/* Coins */}
                                <Grid size={12} sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" color="primary">
                                        {t('cashDrawer.coins')}
                                    </Typography>
                                </Grid>
                                {[
                                    { key: 'bani50', value: 50 },
                                    { key: 'bani10', value: 10 },
                                    { key: 'bani5', value: 5 },
                                    { key: 'bani1', value: 1 },
                                ].map(item => (
                                    <Grid size={6} key={item.key}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={`${item.value} bani`}
                                            value={denominations[item.key as keyof Denominations]}
                                            onChange={e =>
                                                handleDenominationChange(
                                                    item.key as keyof Denominations,
                                                    e.target.value,
                                                )
                                            }
                                            slotProps={{ htmlInput: { min: 0 } }}
                                            size="small"
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6">{t('cashDrawer.totalAmount')}:</Typography>
                                <Typography variant="h6" color="primary">
                                    {actualAmount.toFixed(0)} RON
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Foreign Currency */}
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('cashDrawer.foreignCurrency')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="EUR"
                                        value={euroAmount}
                                        onChange={e =>
                                            setEuroAmount(parseFloat(e.target.value) || 0)
                                        }
                                        slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="USD"
                                        value={usdAmount}
                                        onChange={e =>
                                            setUsdAmount(parseFloat(e.target.value) || 0)
                                        }
                                        slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - Handover Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('cashDrawer.handoverInfo')}
                            </Typography>

                            {balanceInfo && (
                                <Box sx={{ mb: 3 }}>
                                    <Stack spacing={1}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography>
                                                {t('cashDrawer.previousBalance')}:
                                            </Typography>
                                            <Typography fontWeight="bold">
                                                {Number(balanceInfo.previousBalance).toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography>{t('cashDrawer.income')}:</Typography>
                                            <Typography color="success.main">
                                                +{Number(balanceInfo.intrari).toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography>{t('cashDrawer.expenses')}:</Typography>
                                            <Typography color="error.main">
                                                -{Number(balanceInfo.iesiri).toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                        <Divider />
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="h6">
                                                {t('cashDrawer.expected')}:
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                {Number(balanceInfo.expectedBalance).toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="h6">
                                                {t('cashDrawer.actual')}:
                                            </Typography>
                                            <Typography variant="h6" color="secondary">
                                                {actualAmount.toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="h6">
                                                {t('cashDrawer.difference')}:
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color={
                                                    difference === 0 ? 'success.main' : 'error.main'
                                                }
                                            >
                                                {difference.toFixed(0)} RON
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        onClick={handleViewMovements}
                                        fullWidth
                                        sx={{ mt: 2 }}
                                    >
                                        {t('cashDrawer.viewMovements')}
                                    </Button>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        label={t('cashDrawer.from')}
                                        value={fromUser}
                                        disabled
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth data-testid="handover-to-user">
                                        <InputLabel>{t('cashDrawer.to')}</InputLabel>
                                        <Select
                                            value={toUser}
                                            onChange={e => setToUser(e.target.value)}
                                            label={t('cashDrawer.to')}
                                        >
                                            {users
                                                .filter(u => u.username !== fromUser)
                                                .map(user => (
                                                    <MenuItem key={user.id} value={user.username}>
                                                        {user.username}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label={t('cashDrawer.notesRon')}
                                        value={notesRon}
                                        onChange={e => setNotesRon(e.target.value)}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label={t('cashDrawer.notesForeignCurrency')}
                                        value={notesForeignCurrency}
                                        onChange={e => setNotesForeignCurrency(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Transfer Option */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={withTransfer}
                                        onChange={e => setWithTransfer(e.target.checked)}
                                    />
                                }
                                label={t('cashDrawer.withTransfer')}
                            />

                            {withTransfer && (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid size={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>
                                                {t('cashDrawer.transferToCashPoint')}
                                            </InputLabel>
                                            <Select
                                                value={transferToCashPointId ?? ''}
                                                onChange={e =>
                                                    setTransferToCashPointId(Number(e.target.value))
                                                }
                                                label={t('cashDrawer.transferToCashPoint')}
                                            >
                                                {cashPoints
                                                    .filter(cp => cp.id !== cashPointId)
                                                    .map(cp => (
                                                        <MenuItem key={cp.id} value={cp.id}>
                                                            {cp.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>
                                                {t('cashDrawer.transferToUser')}
                                            </InputLabel>
                                            <Select
                                                value={transferToUserId ?? ''}
                                                onChange={e =>
                                                    setTransferToUserId(Number(e.target.value))
                                                }
                                                label={t('cashDrawer.transferToUser')}
                                            >
                                                {users
                                                    .filter(u => u.username !== fromUser)
                                                    .map(user => (
                                                        <MenuItem key={user.id} value={user.id}>
                                                            {user.username}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveHandover}
                            disabled={saving || !toUser || toUser === fromUser}
                            fullWidth
                            data-testid="handover-save"
                        >
                            {saving ? t('cashDrawer.saving') : t('cashDrawer.saveHandover')}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={() => window.print()}
                            fullWidth
                        >
                            {t('cashDrawer.print')}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Cash Difference Registry */}
            <CassaDiffRegistry />

            {/* Movements Dialog */}
            <Dialog
                open={showMovements}
                onClose={() => setShowMovements(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>{t('cashDrawer.sessionMovements')}</DialogTitle>
                <DialogContent>
                    {movements && (
                        <Box>
                            {/* Income Section */}
                            <Accordion defaultExpanded sx={{ mb: 2 }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        {t('cashDrawer.income')}
                                        <Chip
                                            label={`${movements.intrari
                                                .reduce(
                                                    (acc, group) =>
                                                        acc +
                                                        group.summary.reduce(
                                                            (s, item) =>
                                                                s + Number(item.total ?? 0),
                                                            0,
                                                        ),
                                                    0,
                                                )
                                                .toFixed(0)} RON`}
                                            color="success"
                                            size="small"
                                        />
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {movements.intrari.map(group =>
                                        renderMovementGroup(group, 'income'),
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            {/* Expenses Section */}
                            <Accordion defaultExpanded>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        {t('cashDrawer.expenses')}
                                        <Chip
                                            label={`${movements.iesiri
                                                .reduce(
                                                    (acc, group) =>
                                                        acc +
                                                        group.summary.reduce(
                                                            (s, item) =>
                                                                s + Number(item.total ?? 0),
                                                            0,
                                                        ),
                                                    0,
                                                )
                                                .toFixed(0)} RON`}
                                            color="error"
                                            size="small"
                                        />
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {movements.iesiri.map(group =>
                                        renderMovementGroup(group, 'expense'),
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMovements(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={eInvoicePromptOpen}
                onClose={() => setEInvoicePromptOpen(false)}
                data-testid="handover-e-invoice-dialog"
            >
                <DialogTitle>{t('eInvoice.run.handoverTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('eInvoice.run.handoverQuestion')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setEInvoicePromptOpen(false)}
                        disabled={eInvoiceStarting}
                        data-testid="handover-e-invoice-no"
                    >
                        {t('common.no')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void sendEInvoices()}
                        disabled={eInvoiceStarting}
                        data-testid="handover-e-invoice-yes"
                    >
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CashDrawer;
